import { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { useAppSelector, useAppDispatch } from './store/hooks';
import { loginUserSuccess, logout } from './store/slices/authSlice';
import { getStoredToken, resolveToken } from './utils/auth';
import { getBusinessLogoUrl } from './services/businessLogo';
import { getBusinessInfo } from './services/businessInfo';
import { getTicketLayout } from './services/ticketLayout';

// Layout
import MainLayout from './components/layout/MainLayout';

// Small, critical-path pages stay eager so the first paint after
// login isn't gated on a chunk fetch.
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import VerifyPage from './pages/VerifyPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import HomePage from './pages/HomePage';
import NotFoundPage from './pages/NotFoundPage';

// Everything else is route-split. The admin-only pages pull in heavy
// deps (Google Maps, Recharts) that most sessions never touch.
const OrderPage         = lazy(() => import('./pages/OrderPage'));
const DeliveryListPage  = lazy(() => import('./pages/DeliveryListPage'));
const CallListPage      = lazy(() => import('./pages/CallListPage'));
const ReservationsPage  = lazy(() => import('./pages/ReservationsPage'));
const HandoffPage       = lazy(() => import('./pages/HandoffPage'));
const StatsPage         = lazy(() => import('./pages/StatsPage'));
const TrendsPage        = lazy(() => import('./pages/TrendsPage'));
const MenuPage          = lazy(() => import('./pages/MenuPage'));
const SettingsPage      = lazy(() => import('./pages/SettingsPage'));
const IVRPage           = lazy(() => import('./pages/IVRPage'));
const IVRNewPage        = lazy(() => import('./pages/IVRNewPage'));
const DeliverymanPage   = lazy(() => import('./pages/DeliverymanPage'));
const TablesFloorPlanPage = lazy(() => import('./pages/TablesFloorPlanPage'));
const TableDetailPage   = lazy(() => import('./pages/TableDetailPage'));
const LocalOrdersPage   = lazy(() => import('./pages/LocalOrdersPage'));

// Components
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminRoute from './components/auth/AdminRoute';
import MenuRoute from './components/auth/MenuRoute';
import FeedbackMessages from './components/feedback/FeedbackMessages';
import UnansweredCallsWatchdog from './components/watchdog/UnansweredCallsWatchdog';
import OnlineOrderSoundWatchdog from './components/watchdog/OnlineOrderSoundWatchdog';
import FloatingAudioPlayer from './components/common/FloatingAudioPlayer';

function RouteFallback() {
  return (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 120px)' }}>
      <Spinner animation="border" variant="secondary" />
    </div>
  );
}

export default function App() {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);

  // On mount, if we have a stored token, kick the socket middleware
  // immediately by dispatching loginUserSuccess — the auth slice was
  // already seeded optimistically from storage, but the socket
  // middleware only connects when it sees that action. Then validate
  // the token against the server and log out if it's explicitly bad.
  useEffect(() => {
    const token = getStoredToken();
    if (!token) return;
    dispatch(loginUserSuccess(token));
    resolveToken(token)
      .then((res) => {
        if (res.status === 401 || res.status === 403) dispatch(logout());
      })
      .catch(() => {
        // Network/timeout — don't log out on a transient failure.
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Prime the ticket-logo + business-info + ticket-layout caches once
  // after login so the first delivery modal open doesn't flash
  // placeholders while blobs, metadata or the layout JSON download.
  useEffect(() => {
    if (!isAuthenticated) return;
    getBusinessLogoUrl().catch(() => { /* ignore */ });
    getBusinessInfo().catch(() => { /* ignore */ });
    getTicketLayout().catch(() => { /* ignore */ });
  }, [isAuthenticated]);

  return (
    <>
      <FeedbackMessages />
      {isAuthenticated && <UnansweredCallsWatchdog />}
      {isAuthenticated && <OnlineOrderSoundWatchdog />}
      {isAuthenticated && <FloatingAudioPlayer />}
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/login" element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/verify" element={<VerifyPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route element={<ProtectedRoute />}>
            <Route element={<MainLayout />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/order" element={<OrderPage />} />
              <Route path="/deliveries" element={<DeliveryListPage />} />
              <Route path="/calls" element={<CallListPage />} />
              <Route path="/reservations" element={<ReservationsPage />} />
              <Route path="/handoff" element={<HandoffPage />} />
              <Route element={<MenuRoute />}>
                <Route path="/menu" element={<MenuPage />} />
              </Route>
              <Route path="/deliverymen" element={<DeliverymanPage />} />
              <Route path="/tables" element={<TablesFloorPlanPage />} />
              <Route path="/tables/:tableId" element={<TableDetailPage />} />
              <Route path="/local-orders" element={<LocalOrdersPage />} />
              <Route element={<AdminRoute />}>
                <Route path="/stats" element={<StatsPage />} />
                <Route path="/trends" element={<TrendsPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/ivr" element={<IVRPage />} />
                <Route path="/ivr-new" element={<IVRNewPage />} />
              </Route>
            </Route>
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </>
  );
}
